import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { certs, proofs } from './data';
import LazyImg from './LazyImg';
import Reveal from './Reveal';
import SectionHead from './SectionHead';

const gallery = [
  ...proofs
    .filter((item) => item.image)
    .map((item) => ({ src: item.image, title: item.title, meta: item.meta })),
  ...certs.map((item) => ({
    src: item.image,
    title: item.title,
    meta: `${item.organization} · ${item.year}`,
  })),
];

export default function Recognition() {
  const [index, setIndex] = useState(null);
  const active = index === null ? null : gallery[index];

  useEffect(() => {
    if (index === null) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') setIndex(null);
      if (event.key === 'ArrowRight') setIndex((current) => (current + 1) % gallery.length);
      if (event.key === 'ArrowLeft') {
        setIndex((current) => (current - 1 + gallery.length) % gallery.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [index]);

  const open = (src) => {
    const next = gallery.findIndex((item) => item.src === src);
    if (next >= 0) setIndex(next);
  };

  return (
    <section className="section" id="proof">
      <div className="wrap">
        <SectionHead
          index="03"
          label="Proof"
          title="Patents, a paper, certificates."
          lede="Open a scan to read the document."
        />
        <ol className="proofs">
          {proofs.map((item, itemIndex) => (
            <Reveal as="li" key={item.id} delay={itemIndex * 30}>
              <span className="proof__num">{String(itemIndex + 1).padStart(2, '0')}</span>
              <div>
                <h3>{item.title}</h3>
                <p className="proof__meta">{item.meta}</p>
                <p className="proof__text">{item.text}</p>
                {item.image ? (
                  <button type="button" className="textlink" onClick={() => open(item.image)}>
                    View document
                  </button>
                ) : null}
              </div>
            </Reveal>
          ))}
        </ol>
        <p className="quiet">
          Earlier than the lab work, I also played district chess and state-level rollball and softball.
        </p>

        <div className="certs-head">
          <h3 className="subhead">Certificates</h3>
        </div>
        <div className="certs">
          {certs.map((cert, certIndex) => (
            <Reveal key={cert.id} delay={(certIndex % 3) * 50}>
              <button type="button" className="cert" onClick={() => open(cert.image)}>
                <span className="cert__img">
                  <LazyImg src={cert.image} alt="" />
                </span>
                <span className="cert__meta">
                  <strong>{cert.title}</strong>
                  <em>
                    {cert.organization}
                    <span> · {cert.year}</span>
                  </em>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      {active
        ? createPortal(
            <div className="lightbox" role="dialog" aria-modal="true" aria-label={active.title} onClick={() => setIndex(null)}>
              <div className="lightbox__card" onClick={(event) => event.stopPropagation()}>
                <img src={active.src} alt={active.title} />
                <div className="lightbox__bar">
                  <div>
                    <strong>{active.title}</strong>
                    <p>{active.meta}</p>
                  </div>
                  <div className="lightbox__actions">
                    <button type="button" className="btn btn--ghost btn--small" onClick={() => setIndex((current) => (current - 1 + gallery.length) % gallery.length)}>
                      Previous
                    </button>
                    <button type="button" className="btn btn--ghost btn--small" onClick={() => setIndex((current) => (current + 1) % gallery.length)}>
                      Next
                    </button>
                    <button type="button" className="btn btn--solid btn--small" onClick={() => setIndex(null)} autoFocus>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
}
